/** Pure change-set store for the batch tenant-config grid.
 *
 * Accumulates per-tenant, per-concrete-path pending edits with undo/redo,
 * then compiles them into the ONE all-or-nothing `/tour_operators/configs/batch/`
 * request. No React here — `ChangeSetContext.tsx` is the thin wrapper.
 */
import {
  MASKED_SECRET,
  type BatchPersistRequest,
  type BatchTenantChange,
} from '@/api/tourOperatorConfigs';

import { canonicalize, resolveCanonical } from './pathOps';

/** Sentinel meaning "remove this key" (vs setting it to some value). */
export const UNSET: unique symbol = Symbol('config-batch-unset');

export type PendingValue = unknown | typeof UNSET;

export interface PendingEdit {
  /** Baseline value at edit time; `undefined` = key absent at baseline. */
  before: unknown;
  after: PendingValue;
  /** The tenant's `config_last_modified_ts` captured at edit time. */
  baseTs: string | null;
  /** Set by REBASE_TENANT when the fresh value matches neither side. */
  stale?: boolean;
}

export interface HistoryEntryItem {
  tenantId: number;
  path: string;
  /** Entry value before the action (undefined = no entry). */
  prev: PendingEdit | undefined;
  /** Entry value after the action (undefined = no entry). */
  next: PendingEdit | undefined;
}

export interface HistoryEntry {
  entries: HistoryEntryItem[];
}

export interface ChangeSetState {
  /** tenantId → concrete path → pending edit. Empty tenant records are
   *  removed rather than kept as `{}`. */
  edits: Record<number, Record<string, PendingEdit>>;
  undoStack: HistoryEntry[];
  redoStack: HistoryEntry[];
  /** Increments on every state change — the memo signal for hooks. */
  version: number;
}

export const initialChangeSetState: ChangeSetState = {
  edits: {},
  undoStack: [],
  redoStack: [],
  version: 0,
};

export type ChangeSetAction =
  | {
      type: 'SET_EDIT';
      tenantId: number;
      path: string;
      before: unknown;
      after: PendingValue;
      baseTs: string | null;
    }
  | { type: 'REMOVE_EDIT'; tenantId: number; path: string }
  /** Post-rebase conflict resolution: the user has seen the fresh server
   *  value (REBASE_TENANT moved it into `before`/`baseTs`) and still wants
   *  their `after` — acknowledging clears the stale marker. */
  | { type: 'KEEP_MINE'; tenantId: number; path: string }
  | { type: 'RESET_ALL' }
  | { type: 'DROP_TENANT'; tenantId: number }
  | {
      type: 'REBASE_TENANT';
      tenantId: number;
      freshConfig: Record<string, unknown> | null;
      freshTs: string | null;
    }
  | { type: 'UNDO' }
  | { type: 'REDO' };

/** Deep equality over pending values via JSON round-trip. `UNSET` and
 *  `undefined` (key absent) are handled explicitly and considered equal to
 *  each other — unsetting an absent key is a no-op. */
function pendingEq(a: PendingValue, b: PendingValue): boolean {
  const na = a === UNSET ? undefined : a;
  const nb = b === UNSET ? undefined : b;
  if (na === undefined || nb === undefined) return na === nb;
  return JSON.stringify(na) === JSON.stringify(nb);
}

function entriesEqual(a: PendingEdit | undefined, b: PendingEdit | undefined): boolean {
  if (a === undefined || b === undefined) return a === b;
  return (
    pendingEq(a.after, b.after) &&
    pendingEq(a.before, b.before) &&
    a.baseTs === b.baseTs &&
    Boolean(a.stale) === Boolean(b.stale)
  );
}

type EditsMap = ChangeSetState['edits'];

/** Immutably set/delete one entry, dropping empty tenant records. */
function withEntry(
  edits: EditsMap,
  tenantId: number,
  path: string,
  entry: PendingEdit | undefined,
): EditsMap {
  const nextTenant = { ...(edits[tenantId] ?? {}) };
  if (entry === undefined) delete nextTenant[path];
  else nextTenant[path] = entry;
  const next = { ...edits };
  if (Object.keys(nextTenant).length === 0) delete next[tenantId];
  else next[tenantId] = nextTenant;
  return next;
}

/** Apply `items` (their `next` side), push the inverse HistoryEntry, clear
 *  the redo stack, bump the version. */
function pushHistory(state: ChangeSetState, items: HistoryEntryItem[]): ChangeSetState {
  let edits = state.edits;
  for (const item of items) edits = withEntry(edits, item.tenantId, item.path, item.next);
  return {
    edits,
    undoStack: [...state.undoStack, { entries: items }],
    redoStack: [],
    version: state.version + 1,
  };
}

/** One removal item per live entry — the inverse restores everything. */
function allEntryItems(edits: EditsMap): HistoryEntryItem[] {
  const items: HistoryEntryItem[] = [];
  const tenantIds = Object.keys(edits)
    .map(Number)
    .sort((a, b) => a - b);
  for (const tenantId of tenantIds) {
    const tenantEdits = edits[tenantId];
    if (!tenantEdits) continue;
    for (const path of Object.keys(tenantEdits).sort()) {
      items.push({ tenantId, path, prev: tenantEdits[path], next: undefined });
    }
  }
  return items;
}

export function changeSetReducer(state: ChangeSetState, action: ChangeSetAction): ChangeSetState {
  switch (action.type) {
    case 'SET_EDIT': {
      const existing = state.edits[action.tenantId]?.[action.path];
      // An existing entry keeps its ORIGINAL baseline — only `after` moves.
      const before = existing ? existing.before : action.before;
      const baseTs = existing ? existing.baseTs : action.baseTs;
      const next: PendingEdit | undefined = pendingEq(action.after, before)
        ? undefined // editing back to the baseline clears the entry
        : { before, after: action.after, baseTs, ...(existing?.stale ? { stale: true } : {}) };
      if (entriesEqual(existing, next)) return state;
      return pushHistory(state, [
        { tenantId: action.tenantId, path: action.path, prev: existing, next },
      ]);
    }
    case 'REMOVE_EDIT': {
      const existing = state.edits[action.tenantId]?.[action.path];
      if (!existing) return state;
      return pushHistory(state, [
        { tenantId: action.tenantId, path: action.path, prev: existing, next: undefined },
      ]);
    }
    case 'KEEP_MINE': {
      // Only meaningful on a stale entry (SET_EDIT deliberately PRESERVES the
      // stale marker, so re-committing the same value cannot clear it).
      const existing = state.edits[action.tenantId]?.[action.path];
      if (!existing?.stale) return state;
      const next: PendingEdit = {
        before: existing.before,
        after: existing.after,
        baseTs: existing.baseTs,
      };
      return pushHistory(state, [
        { tenantId: action.tenantId, path: action.path, prev: existing, next },
      ]);
    }
    case 'RESET_ALL': {
      const items = allEntryItems(state.edits);
      if (items.length === 0) return state;
      return pushHistory(state, items);
    }
    case 'DROP_TENANT': {
      const tenantEdits = state.edits[action.tenantId];
      if (!tenantEdits) return state;
      const items = Object.keys(tenantEdits)
        .sort()
        .map((path) => ({
          tenantId: action.tenantId,
          path,
          prev: tenantEdits[path],
          next: undefined,
        }));
      return pushHistory(state, items);
    }
    case 'REBASE_TENANT': {
      const tenantEdits = state.edits[action.tenantId];
      const hasHistory = state.undoStack.length > 0 || state.redoStack.length > 0;
      let edits = state.edits;
      let changed = false;
      if (tenantEdits) {
        const nextTenant: Record<string, PendingEdit> = {};
        for (const [path, edit] of Object.entries(tenantEdits)) {
          // Edit paths are concrete (no wildcards) — 0 or 1 resolution.
          const resolved = action.freshConfig ? resolveCanonical(action.freshConfig, path) : [];
          const firstResolved = resolved[0];
          const fresh = firstResolved === undefined ? undefined : firstResolved.value;
          if (pendingEq(fresh, edit.after)) {
            // The server already holds the desired value (for UNSET: the key
            // is gone) — the edit is obsolete, drop it.
            changed = true;
            continue;
          }
          const nextEdit: PendingEdit = pendingEq(fresh, edit.before)
            ? { before: edit.before, after: edit.after, baseTs: action.freshTs }
            : { before: fresh, after: edit.after, baseTs: action.freshTs, stale: true };
          nextTenant[path] = nextEdit;
          if (!entriesEqual(edit, nextEdit)) changed = true;
        }
        if (changed) {
          edits = { ...state.edits };
          if (Object.keys(nextTenant).length === 0) delete edits[action.tenantId];
          else edits[action.tenantId] = nextTenant;
        }
      }
      if (!changed && !hasHistory) return state;
      // Rebasing invalidates recorded inverses (their before/baseTs are
      // outdated) — both stacks are cleared.
      return { edits, undoStack: [], redoStack: [], version: state.version + 1 };
    }
    case 'UNDO': {
      const entry = state.undoStack[state.undoStack.length - 1];
      if (!entry) return state;
      let edits = state.edits;
      for (const item of entry.entries) edits = withEntry(edits, item.tenantId, item.path, item.prev);
      return {
        edits,
        undoStack: state.undoStack.slice(0, -1),
        redoStack: [...state.redoStack, entry],
        version: state.version + 1,
      };
    }
    case 'REDO': {
      const entry = state.redoStack[state.redoStack.length - 1];
      if (!entry) return state;
      let edits = state.edits;
      for (const item of entry.entries) edits = withEntry(edits, item.tenantId, item.path, item.next);
      return {
        edits,
        undoStack: [...state.undoStack, entry],
        redoStack: state.redoStack.slice(0, -1),
        version: state.version + 1,
      };
    }
  }
}

// ---------------------------------------------------------------------------
// Selectors — pure functions over ChangeSetState
// ---------------------------------------------------------------------------

export function pendingTotals(state: ChangeSetState): { edits: number; tenants: number } {
  let edits = 0;
  let tenants = 0;
  for (const tenantEdits of Object.values(state.edits)) {
    const n = Object.keys(tenantEdits).length;
    if (n > 0) {
      tenants += 1;
      edits += n;
    }
  }
  return { edits, tenants };
}

/** Canonical key → number of pending edits (across tenants and indices). */
export function pendingByCanonicalKey(state: ChangeSetState): Map<string, number> {
  const out = new Map<string, number>();
  for (const tenantEdits of Object.values(state.edits)) {
    for (const path of Object.keys(tenantEdits)) {
      const key = canonicalize(path);
      out.set(key, (out.get(key) ?? 0) + 1);
    }
  }
  return out;
}

export function editFor(
  state: ChangeSetState,
  tenantId: number,
  path: string,
): PendingEdit | undefined {
  return state.edits[tenantId]?.[path];
}

export function hasStaleEdits(state: ChangeSetState): boolean {
  for (const tenantEdits of Object.values(state.edits)) {
    for (const edit of Object.values(tenantEdits)) {
      if (edit.stale) return true;
    }
  }
  return false;
}

export interface DiffEntry {
  tenantId: number;
  path: string;
  before: unknown;
  after: PendingValue;
}

/** Flat, deterministic listing of every pending edit (tenantId, then path). */
export function diffEntries(state: ChangeSetState): DiffEntry[] {
  const out: DiffEntry[] = [];
  const tenantIds = Object.keys(state.edits)
    .map(Number)
    .sort((a, b) => a - b);
  for (const tenantId of tenantIds) {
    const tenantEdits = state.edits[tenantId];
    if (!tenantEdits) continue;
    for (const path of Object.keys(tenantEdits).sort()) {
      const edit = tenantEdits[path];
      if (!edit) continue;
      out.push({ tenantId, path, before: edit.before, after: edit.after });
    }
  }
  return out;
}

/** Compile the change set into the all-or-nothing batch request body.
 *
 *  Per tenant: `set` collects non-UNSET values (edits whose value equals the
 *  masked-secret sentinel are skipped — they would be no-ops), `unset`
 *  collects UNSET paths, and `if_unmodified_since` is the tenant's shared
 *  baseTs — the MIN when the edits' baselines differ, `null` if any is null.
 *  Tenants whose edits compile to neither `set` nor `unset` are omitted. */
export function buildPersistRequest(state: ChangeSetState, comment: string): BatchPersistRequest {
  const changes: BatchTenantChange[] = [];
  const tenantIds = Object.keys(state.edits)
    .map(Number)
    .sort((a, b) => a - b);
  for (const tenantId of tenantIds) {
    const tenantEdits = state.edits[tenantId];
    if (!tenantEdits) continue;
    const set: Record<string, unknown> = {};
    const unset: string[] = [];
    const baseTsValues: (string | null)[] = [];
    for (const path of Object.keys(tenantEdits).sort()) {
      const edit = tenantEdits[path];
      if (!edit) continue;
      baseTsValues.push(edit.baseTs);
      if (edit.after === UNSET) unset.push(path);
      else if (edit.after !== MASKED_SECRET) set[path] = edit.after;
    }
    if (Object.keys(set).length === 0 && unset.length === 0) continue;
    let ius: string | null = null;
    if (!baseTsValues.includes(null)) {
      // ISO-8601 timestamps compare lexicographically — MIN is the earliest.
      ius = (baseTsValues as string[]).reduce((min, ts) => (ts < min ? ts : min));
    }
    const change: BatchTenantChange = { tour_operator: tenantId, if_unmodified_since: ius };
    if (Object.keys(set).length > 0) change.set = set;
    if (unset.length > 0) change.unset = unset;
    changes.push(change);
  }
  return { comment, changes };
}
